import {
  Check as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LockOutlined as LockOutlineIcon,
} from "@mui/icons-material";
import { CardContent, Grid, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import DecreaseButton from "components/DecreaseButton";
import IncreaseButton from "components/IncreaseButton";
import { InvoiceAutocomplete } from "components/job/InvoiceAutocomplete";
import { OrderLineInfoInput } from "components/OrderLineInfoInput";
import { PromptDialog } from "components/PromptDialog";
import StyledCard from "components/StyledCard";
import { StyledCardActions } from "components/StyledCardActions";
import StyledTextField from "components/StyledTextField";
import { intlFormatPrice, isPartRequested, isPartReturnRequested, isPartUsed } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { AddPartEnum } from "models/AddPartEnum";
import { UsedPart } from "models/usedPart";
import { Maybe, PartType, StockStore } from "operations/schema/schema";
import { Dispatch, FC, SetStateAction, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { removePart, selectSelectedJobVisit, updatePart } from "store/slices/partner.store";

const PREFIX = "UsedPart";

const classes = {
  buttonWrapper: `${PREFIX}-buttonWrapper`,
  error: `${PREFIX}-error`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  width: "100%",

  [`& .${classes.buttonWrapper}`]: {
    marginTop: "10px",
  },

  [`& .${classes.error}`]: {
    color: theme.palette.error.main,
  },
}));

interface UsedPartProps {
  index: number;
  maxQuantity: number;
  part: PartType;
  usedPart: UsedPart;
  setCountEditItems: (amount: number) => void;
  dialogType: AddPartEnum;
  setTabIndex: Dispatch<SetStateAction<string>>;
  disabled: boolean;
}

export const UsedPartComponent: FC<UsedPartProps> = (props) => {
  const {
    maxQuantity,
    part,
    usedPart,
    setCountEditItems,
    dialogType,
    setTabIndex,
    disabled,
    index,
  } = props;

  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { usedParts } = useAppSelector(selectSelectedJobVisit);
  const { orderLineInfo } = useAppSelector((s) => s.cache);
  const [openEditPart, setOpenEditPart] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [tempQty, setTempQty] = useState<string>((part.quantity || 0).toString());
  const [tempPrice, setTempPrice] = useState<string>((part.salesPrice || 0).toString());
  const [qtyError, setQtyError] = useState<string>("");
  const [priceError, setPriceError] = useState<string>("");
  const allowChangeInvoiceType = technicianSettings.allowChangeInvoiceType;
  const allowNegativeStock = technicianSettings.allowNegativeStock ?? false;
  const allowSalesPrice = technicianSettings.syncCostPrice || technicianSettings.syncCostPrice;
  const allowEditSalesPrice = dialogType === AddPartEnum.AddPart && allowSalesPrice && openEditPart;
  const openEditDescription = openEditPart && part.isNonStock;

  const changeQty = useCallback(
    (quantity: number) => {
      setTempQty(quantity.toString());
      dispatch(
        updatePart({
          index,
          part: {
            ...usedPart,
            part: {
              ...part,
              quantity,
            },
          },
        }),
      );
    },
    [dispatch, index, part, usedPart],
  );

  const changeQtyFromInput = useCallback(
    (value: string) => {
      setTempQty(value);
      const numericValue = part.isNonStock ? parseFloat(value) : parseInt(value);
      var limit = maxQuantity ?? 1000;

      if (numericValue <= limit || allowNegativeStock) {
        changeQty(numericValue);
        setQtyError("");
      } else {
        if (limit >= 1) {
          setQtyError(
            intl.formatMessage(
              {
                id: "part.quantityTooHigh",
              },
              {
                qty: limit,
              },
            ),
          );
        }
      }
    },
    [allowNegativeStock, changeQty, intl, maxQuantity, part.isNonStock],
  );

  const changePriceFromInput = useCallback(
    (value: string) => {
      setTempPrice(value);
      const salesPrice = parseFloat(value);

      if (salesPrice > 0) {
        dispatch(
          updatePart({
            index,
            part: {
              ...usedPart,
              part: {
                ...part,
                salesPrice,
                salesPriceChangedOnClient: true,
              },
            },
          }),
        );
        setPriceError("");
      } else {
        setPriceError(
          intl.formatMessage({
            id: "general.priceGreaterThanZero",
          }),
        );
      }
    },
    [dispatch, index, intl, part, usedPart],
  );

  const changeInvoiceType = useCallback(
    (value: string) => {
      dispatch(
        updatePart({
          index,
          part: {
            ...usedPart,
            part: {
              ...part,
              invoiceType: value,
            },
          },
        }),
      );
    },
    [dispatch, index, part, usedPart],
  );

  const changeDescription = useCallback(
    (value: string) => {
      dispatch(
        updatePart({
          index,
          part: {
            ...usedPart,
            part: {
              ...part,
              description: value,
            },
          },
        }),
      );
    },
    [dispatch, index, part, usedPart],
  );

  const setInfo = useCallback(
    (value: Maybe<string>, type: string) => {
      var info01 = part.info01;
      var info02 = part.info02;
      var info03 = part.info03;
      var info04 = part.info04;
      switch (type) {
        case "1":
          info01 = value;
          break;
        case "2":
          info02 = value;
          break;
        case "3":
          info03 = value;
          break;
        case "4":
          info04 = value;
          break;
      }
      dispatch(
        updatePart({
          index,
          part: {
            ...usedPart,
            part: {
              ...part,
              info01,
              info02,
              info03,
              info04,
            },
          },
        }),
      );
    },
    [dispatch, index, part, usedPart],
  );

  const editJobPart = useCallback(() => {
    setCountEditItems(!openEditPart ? +1 : -1);
    setTempQty((part.quantity || 0).toString());
    setQtyError("");
    setOpenEditPart(!openEditPart);
  }, [openEditPart, part.quantity, setCountEditItems]);

  const removeJobPart = useCallback(() => {
    dispatch(removePart({ index }));
    if (!usedParts.some((p) => isPartReturnRequested(p.part!))) {
      setTabIndex("usedParts");
    }
  }, [dispatch, index, setTabIndex, usedParts]);

  if (maxQuantity === undefined && isPartUsed(part)) {
    return null;
  }
  const isAddButtonDisabled =
    !!maxQuantity && (part.quantity || 0) >= maxQuantity && !allowNegativeStock;

  const stockLabelTranslatable =
    part.storeLabel === StockStore.Engineer
      ? intl.formatMessage({ id: "general.engineer" }).toUpperCase()
      : part.storeLabel === StockStore.Locational
        ? intl.formatMessage({ id: "part.locational" }).toUpperCase()
        : part.storeLabel === StockStore.Nonstock
          ? intl.formatMessage({ id: "part.nonStock" }).toUpperCase()
          : intl.formatMessage({ id: "part.other" }).toUpperCase();

  return (
    <StyledGrid item xs={12} sm={6} lg={4}>
      <StyledCard key={part.articleNo}>
        <CardContent sx={{ width: "100%" }}>
          {!openEditDescription ? (
            <Typography variant="body1" component="h3" data-testid="UsedPart-Description">
              <FormattedMessage id="general.description" />: {part.description}
            </Typography>
          ) : (
            <Grid item container xs={12}>
              <StyledTextField
                fullWidth
                value={part.description}
                id="description"
                label={intl.formatMessage({ id: "general.description" })}
                onChange={(e: any) => changeDescription(e.target.value)}
                multiline
                rows={5}
              />
            </Grid>
          )}
          <Typography color="textSecondary">{part.articleNo}</Typography>
          {dialogType === AddPartEnum.AddPart && (
            <Typography color="textSecondary">
              {isPartRequested(part)
                ? intl.formatMessage({ id: "part.requested" })
                : stockLabelTranslatable}
            </Typography>
          )}
          {allowSalesPrice && part.salesPrice && (
            <>
              <Typography variant="body2" component="p">
                <FormattedMessage id="general.salesPrice" />:{" "}
                {!allowEditSalesPrice && intlFormatPrice(intl, part.salesPrice)}
              </Typography>
              {allowEditSalesPrice && (
                <Grid container mt={1} mb={1}>
                  <Grid item textAlign="center">
                    <StyledTextField
                      value={tempPrice}
                      id="salesPrice"
                      type="number"
                      onChange={(event) => changePriceFromInput(event.target.value)}
                      helperText={priceError}
                      error={priceError !== ""}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}
          {!openEditPart && (
            <Typography variant="body2" component="p">
              <FormattedMessage id="general.quantity" />: {part.quantity}
            </Typography>
          )}
          {!openEditPart && allowChangeInvoiceType && (
            <Typography variant="body2" component="p">
              <FormattedMessage id="visit.invoice" />:{" "}
              {part.invoiceType ?? <FormattedMessage id="visit.contract" />}
            </Typography>
          )}
          {!openEditPart && part.info01 && (
            <Typography variant="body2" component="p">
              {orderLineInfo.info01?.description}: {part.info01}
            </Typography>
          )}
          {!openEditPart && part.info02 && (
            <Typography variant="body2" component="p">
              {orderLineInfo.info02?.description}: {part.info02}
            </Typography>
          )}
          {!openEditPart && part.info03 && (
            <Typography variant="body2" component="p">
              {orderLineInfo.info03?.description}: {part.info03}
            </Typography>
          )}
          {!openEditPart && part.info04 && (
            <Typography variant="body2" component="p">
              {orderLineInfo.info04?.description}: {part.info04}
            </Typography>
          )}
          {openEditPart && (
            <Grid container spacing={2}>
              <Grid item>
                <Typography variant="body2" component="p">
                  <FormattedMessage id="general.quantity" />:
                </Typography>
              </Grid>
              <Grid
                container
                direction="row"
                justifyContent="space-around"
                alignItems="center"
                className={classes.buttonWrapper}
                wrap="nowrap"
              >
                <Grid item xs={2}>
                  <DecreaseButton
                    className={part.quantity === 1 ? "buttonDisabled" : ""}
                    onClick={() => changeQty((part.quantity || 0) - 1)}
                  />
                </Grid>
                <Grid item xs={5} textAlign="center">
                  <StyledTextField
                    value={tempQty}
                    id="quantity"
                    type="number"
                    onChange={(event) => changeQtyFromInput(event.target.value)}
                    helperText={qtyError}
                    error={qtyError !== ""}
                    style={{ width: "70%" }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IncreaseButton
                    disabled={isAddButtonDisabled}
                    className={isAddButtonDisabled ? "buttonDisabled" : ""}
                    onClick={() => changeQty((part.quantity || 0) + 1)}
                  />
                </Grid>
              </Grid>
              {allowChangeInvoiceType && (
                <Grid item container xs={12}>
                  <InvoiceAutocomplete
                    onChange={changeInvoiceType}
                    value={part.invoiceType ?? null}
                  />
                </Grid>
              )}
              <OrderLineInfoInput
                info01={part.info01 ?? null}
                info02={part.info02 ?? null}
                info03={part.info03 ?? null}
                info04={part.info04 ?? null}
                setInfo01={(value) => setInfo(value, "1")}
                setInfo02={(value) => setInfo(value, "2")}
                setInfo03={(value) => setInfo(value, "3")}
                setInfo04={(value) => setInfo(value, "4")}
              />
            </Grid>
          )}
        </CardContent>
        <StyledCardActions>
          {disabled && (
            <IconButton disabled>
              <LockOutlineIcon color="primary" />
            </IconButton>
          )}
          {!disabled && (
            <>
              <IconButton
                aria-label={intl.formatMessage({ id: "general.delete" })}
                onClick={() => setConfirmationDialog(true)}
                size="large"
              >
                <DeleteIcon color="error" />
              </IconButton>
              {openEditPart ? (
                <IconButton
                  aria-label={intl.formatMessage({ id: "general.save" })}
                  onClick={editJobPart}
                  size="large"
                >
                  <CheckIcon color="primary" />
                </IconButton>
              ) : (
                <IconButton
                  aria-label={intl.formatMessage({ id: "general.edit" })}
                  onClick={editJobPart}
                  size="large"
                >
                  <EditIcon color="action" />
                </IconButton>
              )}
            </>
          )}
        </StyledCardActions>
      </StyledCard>
      <PromptDialog
        aria-labelledby="form-dialog-title"
        open={confirmationDialog}
        setOpen={setConfirmationDialog}
        okText={intl.formatMessage({ id: "general.confirm" })}
        onOk={removeJobPart}
        title={<FormattedMessage id="part.remove" />}
        promptContent={<FormattedMessage id="visit.part.removePrompt" />}
      />
    </StyledGrid>
  );
};
